import React from 'react';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckIcon from '@material-ui/icons/Check';
import customStyle from '../../Assets/CSS/stylesheet_UHS';

// Shared between EnrollmentSummary.js, PlanSelection.js (Review Choices popup) and
// ReviewChoice.js (Review Choices step) — one rendering implementation so all three surfaces
// present PROGRAM/PRODUCT SUMMARY and FAMILY DETAILS identically ("plan wise" collapsible
// sections, green-check family matrix), each fed by their own already-fetched data.

// Line item text (e.g. "Monthly Share Contribution for Thrive Healthshare 50") splits on
// " for " into the charge description and the plan name, with the plan name set off in bold —
// falls back to the plain text unsplit when it doesn't match that "<desc> for <plan>" shape.
function formatLineItemText(text) {
    if (!text) return text;
    const match = text.match(/^(.*?)\s+for\s+(.+)$/i);
    if (!match) return text;
    return <>{match[1]} for <b>{match[2]}</b></>;
}

// The actual plan name (e.g. "Thrive Healthshare 50") for a section — section.title is
// sometimes a generic carrier-level label ("Sharing Programs") rather than the specific plan,
// so this prefers pulling it out of a row's own "<desc> for <plan>" text first, falling back to
// the section title (with any "UHS " carrier prefix dropped) only when no row matches that shape.
function getSectionPlanName(section) {
    if (!section) return null;
    const rows = section.rows || [];
    for (const row of rows) {
        const match = row.text && row.text.match(/\bfor\s+(.+)$/i);
        if (match) return match[1];
    }
    return section.title ? section.title.replace(/^UHS\s+/i, '') : null;
}

export function renderProgramSummaryTable({
    reviewSummaryData,
    expandedSections,
    onToggleSection,
    firstPaymentAmount,
    applicationFee,
    firstPaymentDateFormatted,
    draftDayFormatted,
}) {
    if (!reviewSummaryData) {
        return <div style={{ padding: '20px', fontSize: '14px', fontFamily: 'Roboto, Arial, Helvetica, sans-serif' }}>No data available.</div>;
    }
    const { body } = reviewSummaryData;
    // Section title + its total row are merged into one collapsible header row (chevron toggles
    // the line items below), so there's no separate gray title bar.
    const sectionHeaderCellStyle = { padding: '10px 24px', fontSize: '15px', color: '#19191d', fontWeight: 'bold', backgroundColor: '#f9fafc', borderTop: '1px solid #8fa0b0', borderBottom: '1px solid #8fa0b0' };
    const grandTotalCellStyle = { padding: '10px 24px', fontSize: '17px', fontWeight: 'bold', color: '#ffffff', backgroundColor: '#3499ce' };
    const sections = body.filter((item) => Array.isArray(item.rows));
    const grandTotalEntry = body.find((item) => !Array.isArray(item.rows));
    // "Application Fee - <plan name>" — the fee is for the primary plan, i.e. this response's
    // first section (e.g. "Thrive Healthshare 50").
    const uhfSharingProgramsSection =  sections[0] && sections[0].title && sections[0].title.toLowerCase() === "sharing programs";
    const applicationFeePlanName = getSectionPlanName(sections[0]);
    const parseMoneyAmount = (str) => (!str || str === '-') ? 0 : (parseFloat(String(str).replace(/[^0-9.-]/g, '')) || 0);
    const formatMoneyAmount = (num) => '$' + num.toFixed(2);
    let grandRegularTotal = 0;
    if (!grandTotalEntry) {
        sections.forEach((section) => {
            if (!section.rows.length) return;
            const totalRow = section.rows[section.rows.length - 1];
            grandRegularTotal += parseMoneyAmount(totalRow.regularAmount);
        });
    }
    const recurringAmount = grandTotalEntry ? grandTotalEntry.regularAmount : formatMoneyAmount(grandRegularTotal);
    const recurringKeyDate = draftDayFormatted ? `${draftDayFormatted} of the month` : '-';
    return (
        <div>
            <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                <div>
                    <table style={customStyle.reviewChoicesTable}>
                        <thead>
                            <tr>
                                <th style={customStyle.FamilyTabTableHeaderCell}>Program / Plan</th>
                                <th style={customStyle.FamilyTabTableHeaderCell}>Amount</th>
                                <th style={customStyle.FamilyTabTableHeaderCell}>Key Dates</th>
                            </tr>
                        </thead>
                        <tbody>
                            {uhfSharingProgramsSection && firstPaymentAmount != null &&
                                <>
                                    <tr>
                                        <td style={grandTotalCellStyle}>Your First Payment</td>
                                        <td style={grandTotalCellStyle}>{formatMoneyAmount(parseMoneyAmount(recurringAmount) + (applicationFee || 0))}</td>
                                        <td style={grandTotalCellStyle}>{firstPaymentDateFormatted || '-'}</td>
                                    </tr>
                                    {applicationFee != null &&
                                        <tr>
                                            <td style={{ ...customStyle.reviewChoicesTableCell, fontWeight: 'bold' }}>{applicationFeePlanName ? `Application Fee - ${applicationFeePlanName}` : 'Application Fee'}</td>
                                            <td style={{ ...customStyle.reviewChoicesTableCell, fontWeight: 'bold' }}>{formatMoneyAmount(applicationFee)}</td>
                                            <td style={{ ...customStyle.reviewChoicesTableCell, fontWeight: 'bold' }}></td>
                                        </tr>
                                    }
                                </>
                            }
                            <tr>
                                <td style={grandTotalCellStyle}>Monthly Payment</td>
                                <td style={grandTotalCellStyle}>{recurringAmount}</td>
                                <td style={grandTotalCellStyle}>{recurringKeyDate}</td>
                            </tr>
                            {sections.map((section, sectionIndex) => {
                                const rows = section.rows || [];
                                // "One Time Application Fee" is already shown as its own top-level
                                // "Application Fee - <plan>" row above — drop it from the expanded
                                // line items so it isn't shown twice.
                                const lineItems = rows.slice(0, -1).filter((row) => !/one[-\s]?time\s+application\s+fee/i.test(row.text || ''));
                                const totalRow = rows.length ? rows[rows.length - 1] : null;
                                const collapsible = lineItems.length > 0;
                                const expanded = expandedSections[sectionIndex] === true;
                                const headerCellStyle = collapsible ? { ...sectionHeaderCellStyle, cursor: 'pointer' } : sectionHeaderCellStyle;
                                // section.title is sometimes a generic carrier-level label (e.g.
                                // "Sharing Programs") rather than the specific plan — append the
                                // plan name pulled from this section's own rows so the header
                                // reads "Sharing Programs - Thrive Healthshare 50".
                                const sectionPlanName = getSectionPlanName(section);
                                const sectionLabel = sectionPlanName && sectionPlanName !== section.title
                                    ? `${section.title} - ${sectionPlanName}`
                                    : section.title;
                                return (
                                    <React.Fragment key={sectionIndex}>
                                        <tr onClick={collapsible ? () => onToggleSection(sectionIndex) : undefined}>
                                            <td style={headerCellStyle}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    {collapsible &&
                                                        <ExpandMoreIcon style={{ fontSize: '20px', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s ease' }} />
                                                    }
                                                    {collapsible ? sectionLabel : rows[0].text}
                                                </div>
                                            </td>
                                            <td style={headerCellStyle}>{totalRow ? totalRow.regularAmount : ''}</td>
                                            <td style={headerCellStyle}></td>
                                        </tr>
                                        {rows.length === 0 &&
                                            <tr>
                                                <td colSpan={3} style={customStyle.reviewChoicesTableCell}>No selections yet.</td>
                                            </tr>
                                        }
                                        {collapsible && expanded && lineItems.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                <td style={customStyle.reviewChoicesTableCell}>{formatLineItemText(row.text)}</td>
                                                <td style={customStyle.reviewChoicesTableCell}>{row.regularAmount}</td>
                                                <td style={customStyle.reviewChoicesTableCell}></td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// FAMILY DETAILS for a single-carrier context (Review Choices popup/step) — same "Family
// Members Enrolled" matrix look as Enrollment Summary's page, but fed directly by
// review-choices' own familyBreakdown (no separate familyPackeage API call, which wasn't
// reliably populated per-carrier). familyBreakdown is a flat, member-per-ROW shape (header:
// ["Family Member", "<plan name>", "Smoking Surcharge", "Total"], rows: one per member plus a
// trailing {name: "Family Total", ...} row) — member NAMES come from those rows instead of an
// object of member-id keys, and since this view is scoped to one carrier/plan, every member is
// on that one plan, so every column gets a check on the single plan row.
export function renderFamilyBreakdownTable({ reviewFamilyData, reviewSummaryData }) {
    if (!reviewFamilyData) {
        return <div style={{ padding: '20px', fontSize: '14px', fontFamily: 'Roboto, Arial, Helvetica, sans-serif' }}>No data available.</div>;
    }
    const familyHeader = reviewFamilyData.header || [];
    const rows = reviewFamilyData.rows || [];
    const memberRows = rows.filter((row) => row.name !== 'Family Total');
    const planName = familyHeader[1] || 'Plan';
    // Same "<carrier label> - <plan name>" combo Program Summary's section header uses (e.g.
    // "Sharing Programs - Thrive Healthshare 50"), shown as a sub-line under "Complete Health
    // Package" — pulled from the matching review-choices section when available.
    const summarySection = reviewSummaryData && reviewSummaryData.body
        ? reviewSummaryData.body.find((item) => Array.isArray(item.rows))
        : null;
    const summaryPlanName = summarySection ? getSectionPlanName(summarySection) : null;
    const packageSubLabel = summarySection && summaryPlanName && summaryPlanName !== summarySection.title
        ? `${summarySection.title} - ${summaryPlanName}`
        : null;
    const memberHeaderCellStyle = { padding: '10px 24px', fontSize: '15px', fontWeight: 'bold', color: '#ffffff', backgroundColor: '#3499ce', textAlign: 'center' };
    const memberHeaderTitleCellStyle = { ...memberHeaderCellStyle, textAlign: 'left' };
    const sectionCellStyle = { padding: '10px 24px', fontSize: '15px', color: '#19191d', backgroundColor: '#f9fafc', borderTop: '1px solid #8fa0b0', borderBottom: '1px solid #8fa0b0' };
    const checkCellStyle = { ...sectionCellStyle, textAlign: 'center' };
    return (
        <div>
            <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                <div>
                    <table style={customStyle.reviewChoicesTable}>
                        <thead>
                            <tr>
                                <th style={customStyle.FamilyTabTableHeaderCell}>{familyHeader[0] || 'Family Member'}</th>
                                <th colSpan={memberRows.length} style={{ ...customStyle.FamilyTabTableHeaderCell, textAlign: 'center' }}>Family Members Enrolled</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={memberHeaderTitleCellStyle}>
                                    Complete Health Package
                                    {/* {packageSubLabel &&
                                        <div style={{ fontWeight: 'normal', fontSize: '13px' }}>{packageSubLabel}</div>
                                    } */}
                                </td>
                                {memberRows.map((row, rowIndex) => (
                                    <td key={row.memberId || rowIndex} style={memberHeaderCellStyle}>{row.name}</td>
                                ))}
                            </tr>
                            <tr>
                                <td style={sectionCellStyle}><b>{packageSubLabel}</b></td>
                                {memberRows.map((row, rowIndex) => (
                                    <td key={row.memberId || rowIndex} style={checkCellStyle}>
                                        <CheckIcon style={{ color: '#2fb344', fontSize: '22px' }} />
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
